import { HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalInterceptorAuthRequest, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { JWTTokenService } from '../services/jwttoken.service';
import { environment } from 'src/environments/environment';
import { ToasterService } from 'src/app/toaster/toaster.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private msalBroadcastService: MsalBroadcastService,
    private toasterService: ToasterService,
    private jwtTokenService: JWTTokenService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const sanitizedRequest = this.sanitizeQueryParams(req);

    if (sanitizedRequest.responseType === 'text') {
      return next.handle(sanitizedRequest);
    }

    const scopes: string[] = environment.apiConfig.scopes;
    let account: any;
    let isExpired = this.jwtTokenService.isTokenExpired();
    if (!isExpired) {
      const headers = sanitizedRequest.headers.set(
        "Authorization",
        `Bearer ${this.jwtTokenService.jwtToken}`
      );

      return this.sendRequest(sanitizedRequest.clone({ headers }), next);
    }
    else {
      if (this.authService.instance.getActiveAccount()) {
        this.authService
          .getLogger()
          .verbose("Interceptor - active account selected");
        // account = this.authService.instance.getActiveAccount();
        account =
          this.authService.instance.getActiveAccount() ??
          this.authService.instance.getAllAccounts()[0];

        if (!account) {
          // User is not logged in yet.
          // Let the request continue (or return EMPTY depending on your design).
          return next.handle(sanitizedRequest);
        }

      } else {
        this.authService
          .getLogger()
          .verbose("Interceptor - no active account, fallback to first account");
        // account = this.authService.instance.getAllAccounts()[0];
        account =
          this.authService.instance.getActiveAccount() ??
          this.authService.instance.getAllAccounts()[0];

        if (!account) {
          // User is not logged in yet.
          // Let the request continue (or return EMPTY depending on your design).
          return next.handle(sanitizedRequest);
        }
      }
      return this.acquireToken(this.msalGuardConfig.authRequest, scopes, account).pipe(
        switchMap((result: AuthenticationResult) => {
          this.authService
            .getLogger()
            .verbose("Interceptor - setting authorization headers");
          this.jwtTokenService.setToken(result.accessToken);

          const headers = req.headers.set(
            "Authorization",
            `Bearer ${result.accessToken}`
          );

          return this.sendRequest(sanitizedRequest.clone({ headers }), next);
        })
      );
    }
  }

  private sanitizeQueryParams(req: HttpRequest<any>): HttpRequest<any> {
    const sanitizedUrl = this.sanitizeUrl(req.urlWithParams);

    if (sanitizedUrl === req.urlWithParams) {
      return req;
    }

    return req.clone({
      url: sanitizedUrl,
      params: new HttpParams(),
    });
  }

  private sanitizeUrl(url: string): string {
    const hashIndex = url.indexOf('#');
    const urlWithoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
    const queryIndex = urlWithoutHash.indexOf('?');

    if (queryIndex === -1) {
      return url;
    }

    const baseUrl = urlWithoutHash.slice(0, queryIndex);
    const rawQuery = urlWithoutHash.slice(queryIndex + 1);

    if (!rawQuery) {
      return url;
    }

    const sanitizedQuery = rawQuery
      .split('&')
      .filter((segment) => segment.length > 0)
      .map((segment) => {
        const separatorIndex = segment.indexOf('=');

        if (separatorIndex === -1) {
          return this.encodeQueryComponent(segment);
        }

        const rawKey = segment.slice(0, separatorIndex);
        const rawValue = segment.slice(separatorIndex + 1);

        return `${this.encodeQueryComponent(rawKey)}=${this.encodeQueryComponent(rawValue)}`;
      })
      .join('&');

    return `${baseUrl}?${sanitizedQuery}${hash}`;
  }

  private encodeQueryComponent(value: string): string {
    const normalizedValue = this.safeDecodeURIComponent(value.replace(/\+/g, ' '));
    const strippedControlChars = normalizedValue.replace(/[\u0000-\u001F\u007F]/g, '');

    return encodeURIComponent(strippedControlChars);
  }

  private safeDecodeURIComponent(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  sendRequest(
    req: HttpRequest<any>,
    next: HttpHandler): Observable<HttpEvent<any>> {

    // No headers allowed in npm search request
    // const noHeaderReq = req.clone({ headers: new HttpHeaders() });

    return next.handle(req).pipe(
      tap(event => {
        // There may be other events besides the response.
      }, error => {
        if (error.status === 401 && error.statusText.toLowerCase() === "unauthorized") {
          // this.router.navigate(['unauthorized']);
        }
      })
    );
  }
  /**
  * Try to acquire token silently. Invoke interaction if acquireTokenSilent rejected with error or resolved with null access token
  * @param authRequest Request
  * @param scopes Array of scopes for the request
  * @param account Account
  * @returns Authentication result
  */
  private acquireToken(
    authRequest: any,
    scopes: string[],
    account: AccountInfo
  ): Observable<AuthenticationResult> {
    // Note: For MSA accounts, include openid scope when calling acquireTokenSilent to return idToken
    return this.authService
      .acquireTokenSilent({ ...authRequest, scopes, account })
      .pipe(
        catchError(() => {
          this.authService
            .getLogger()
            .error(
              "Interceptor - acquireTokenSilent rejected with error. Invoking interaction to resolve."
            );
          return this.msalBroadcastService.inProgress$.pipe(
            take(1),
            switchMap((status: InteractionStatus) => {
              if (status === InteractionStatus.None) {
                this.acquireTokenInteractively(authRequest, scopes);
              }

              return this.msalBroadcastService.inProgress$.pipe(
                filter(
                  (status: InteractionStatus) =>
                    status === InteractionStatus.None
                ),
                take(1),
                switchMap(() => this.acquireToken(authRequest, scopes, account))
              );
            })
          );
        }),
        switchMap((result: AuthenticationResult) => {
          if (!result.accessToken) {
            this.authService
              .getLogger()
              .error(
                "Interceptor - acquireTokenSilent resolved with null access token. Known issue with B2C tenants, invoking interaction to resolve."
              );
            return this.msalBroadcastService.inProgress$.pipe(
              filter(
                (status: InteractionStatus) => status === InteractionStatus.None
              ),
              take(1),
              switchMap(() =>
                this.acquireTokenInteractively(authRequest, scopes)
              )
            );
          }
          return of(result);
        })
      );
  }

  /**
   * Invoke interaction for the given set of scopes
   * @param authRequest Request
   * @param scopes Array of scopes for the request
   * @returns Result from the interactive request
   */
  private acquireTokenInteractively(
    authRequest: MsalInterceptorAuthRequest,
    scopes: string[]
  ): Observable<AuthenticationResult> {

    this.authService
      .getLogger()
      .verbose(
        "Interceptor - error acquiring token silently, acquiring by redirect"
      );
    const redirectStartPage = window.location.origin;
    this.authService.acquireTokenRedirect({
      ...authRequest,
      scopes,
      redirectStartPage,
    });
    return EMPTY;
  }
}
